import {
  ETICHETA_STARE_TASK,
  type ActiuneTask,
  type DepartamentSlug,
  type StareTask,
} from '@b4l/shared'
import type { ColoanaTablou } from '../../components/ui/Tablou'
import type { CardTask } from './date'

/**
 * Culoarea fiecarui departament — doar pe cardurile de sus si pe eticheta de departament de pe
 * card (cand se vad mai multe departamente deodata). Nicaieri nu inseamna stare.
 */
export const CULOARE_DEPARTAMENT: Record<
  DepartamentSlug,
  { bara: string; bulina: string; inel: string }
> = {
  contabilitate: {
    bara: 'border-l-b4l-purple',
    bulina: 'bg-b4l-purple',
    inel: 'border-b4l-purple ring-b4l-purple',
  },
  vanzari: {
    bara: 'border-l-b4l-blue',
    bulina: 'bg-b4l-blue',
    inel: 'border-b4l-blue ring-b4l-blue',
  },
  marketing: {
    bara: 'border-l-b4l-green',
    bulina: 'bg-b4l-green',
    inel: 'border-b4l-green ring-b4l-green',
  },
  depozit: {
    bara: 'border-l-b4l-amber',
    bulina: 'bg-b4l-amber',
    inel: 'border-b4l-amber ring-b4l-amber',
  },
  administrativ: {
    bara: 'border-l-b4l-red',
    bulina: 'bg-b4l-red',
    inel: 'border-b4l-red ring-b4l-red',
  },
}

/**
 * Un task privat fara departament: gri — mov e al Contabilitatii. Privatul se recunoaste dupa
 * lacat, nu dupa culoare.
 */
export const CULOARE_PRIVAT = {
  bara: 'border-l-b4l-text-muted',
  bulina: 'bg-b4l-text-muted',
  inel: 'border-b4l-text-muted ring-b4l-text-muted',
}

/**
 * Coloanele tabloului. „Gata" a stat sub tablou (12.10.2026); din 27.10.2026 e coloana a patra,
 * langa celelalte — dintr-o coloana lunga nu mai ajungeai sa tragi un card in banda de jos.
 */
export type StareTablou = Exclude<StareTask, 'cancelled'>

export const COLOANE: (ColoanaTablou<StareTablou> & { vechiDupa: number })[] = [
  {
    id: 'de_preluat',
    nume: ETICHETA_STARE_TASK.de_preluat,
    bulina: 'bg-b4l-amber',
    ajutor: 'A intrat la departament și încă nu l-a luat nimeni. Îl ia cine are timp, cu „Preia”.',
    gol: 'Nimic de preluat — tot ce a intrat are pe cineva.',
    vechiDupa: 2,
  },
  {
    id: 'in_progress',
    nume: ETICHETA_STARE_TASK.in_progress,
    bulina: 'bg-b4l-blue',
    ajutor: 'Preluat — se lucrează la el. „Preia” aduce task-ul direct aici.',
    gol: 'Nu se lucrează acum la nimic.',
    vechiDupa: 5,
  },
  {
    id: 'in_asteptare',
    nume: ETICHETA_STARE_TASK.in_asteptare,
    bulina: 'bg-b4l-purple',
    ajutor:
      'Așteaptă pe cineva din afară sau din alt departament. Revine singur în „În lucru” la data aleasă, sau când celălalt departament îl termină.',
    gol: 'Nu se așteaptă pe nimeni.',
    // Asteptarea are data ei de revenire; vechimea nu e semnal aici.
    vechiDupa: Infinity,
  },
]

/** „Finalizate”: terminate in ultimele 7 zile, cu verde. Cele mai vechi stau in Arhiva. */
export const COLOANA_GATA: ColoanaTablou<StareTablou> & { vechiDupa: number } = {
  id: 'done',
  nume: 'Finalizate',
  bulina: 'bg-b4l-green',
  ajutor:
    'Terminate în ultimele 7 zile. Trage aici un card ca să-l închei. Tot ce e mai vechi stă în Arhivă.',
  gol: 'Nimic terminat în ultimele 7 zile.',
  vechiDupa: Infinity,
}

/**
 * Tenta cardului, in culoarea coloanei lui — ca sa-ti dai seama dintr-o privire unde e fiecare,
 * si cand coloanele sunt lungi (Madalin, 14.10.2026). Usoara: rosul ramane semnalul pentru
 * urgent si depasit, nu culoarea de fond a muncii obisnuite.
 */
/**
 * Task legat de bani — plati, facturi, incasari, stornari, TVA, ANAF, deconturi. Criteriul e
 * exact, nu ghicit din cuvinte: clasificatorul de email trimite ORICE email despre bani la
 * Contabilitate (regula de precedenta), iar la privatele din contact@ departamentul ramane ca
 * „propus". Victor, 20.10.2026: cardul lor sa iasa rosu, in orice stare.
 */
export function esteFinanciar(t: Pick<CardTask, 'departament' | 'departamentPropus'>): boolean {
  return t.departament === 'contabilitate' || t.departamentPropus === 'contabilitate'
}

/** Fundalul unui card financiar — acelasi in orice coloana. */
export const FUNDAL_FINANCIAR = 'bg-b4l-red-light border-b4l-red/40'

export const FUNDAL_CARD: Record<StareTablou, string> = {
  de_preluat: 'bg-b4l-amber-light border-b4l-amber/30',
  // Task-urile vechi „De făcut" stau acum in „În lucru" — aceeasi tenta.
  todo: 'bg-b4l-blue-light border-b4l-blue/30',
  in_progress: 'bg-b4l-blue-light border-b4l-blue/30',
  in_asteptare: 'bg-b4l-purple-light border-b4l-purple/30',
  done: 'bg-b4l-green-light border-b4l-green/30',
}

/** Pasii din capul panoului — drumul firesc al unui task. */
export const PASI: StareTablou[] = ['de_preluat', 'in_progress', 'in_asteptare', 'done']

/**
 * Coloana in care sta un task pe tablou. Fara „De făcut" (Madalin, 14.10.2026): cele vechi,
 * ramase `todo`, stau in „În lucru" — sunt preluate, deci asta sunt.
 */
export const coloanaPeTablou = (stare: StareTask): StareTablou =>
  stare === 'todo' ? 'in_progress' : (stare as StareTablou)

/**
 * Ordinea cardurilor deschise pe tablou: dupa cand a intrat fiecare IN COLOANA — exact zilele
 * scrise pe card. Pana pe 19.10.2026 era data crearii, iar un task vechi preluat ieri statea sus
 * cu „1 zi", deci „Vechi" parea amestecat (Madalin). Fiecare coloana cu sensul ei (14.10.2026);
 * urgentele nu sar in fata — se vad dupa bara rosie. La egalitate, id-ul: ordinea nu sare la
 * reimprospatare. Tabloul pastreaza ordinea primita cand imparte pe coloane.
 */
export function ordoneazaPeTablou(
  taskuri: CardTask[],
  ordini: Partial<Record<StareTablou, 'vechi' | 'noi'>>,
): CardTask[] {
  return taskuri
    .map((t) => ({
      t,
      k: (ordini[coloanaPeTablou(t.stare)] === 'noi' ? -1 : 1) * new Date(t.inColoanaDin).getTime(),
    }))
    .sort((a, b) => a.k - b.k || a.t.id - b.t.id)
    .map((x) => x.t)
}

/**
 * Ce actiune inseamna mutarea unui card in coloana `spre`. `null` = nu se poate ajunge acolo
 * din starea lui (ex. din „Gata" direct in „Așteaptă" — nu exista asa ceva in reguli).
 */
export function actiuneSpre(stare: StareTask, spre: StareTablou): ActiuneTask | null {
  switch (spre) {
    case 'de_preluat':
      return ['todo', 'in_progress', 'in_asteptare'].includes(stare) ? 'renunta' : null
    case 'todo':
      return null
    case 'in_progress':
      return stare === 'in_asteptare'
        ? 'reia'
        : stare === 'de_preluat'
          ? 'preia'
          : stare === 'done'
            ? 'redeschide'
            : null
    case 'in_asteptare':
      return stare === 'in_asteptare' ? null : 'asteapta'
    case 'done':
      return stare === 'done' ? null : 'gata'
  }
}

/** Coloanele in care omul poate muta cardul ACUM — doar cele pe care serverul i le permite. */
export function tinteCard(t: CardTask): StareTablou[] {
  return [...COLOANE.map((c) => c.id), 'done' as const].filter((spre) => {
    const a = actiuneSpre(t.stare, spre)
    return a != null && t.actiuni.includes(a)
  })
}

/** Butonul rapid de pe card: pasul firesc urmator, daca omul il poate face. */
export const PAS_URMATOR: Partial<Record<StareTask, { actiune: ActiuneTask; eticheta: string }>> = {
  de_preluat: { actiune: 'preia', eticheta: 'Preia' },
  // Cele vechi „De făcut" stau in „În lucru": pasul urmator e tot „Gata".
  todo: { actiune: 'gata', eticheta: 'Gata' },
  in_progress: { actiune: 'gata', eticheta: 'Gata' },
  in_asteptare: { actiune: 'reia', eticheta: 'Reia' },
}

/**
 * Fereastra „Gata", cu nota optionala de final. Aceeasi din panou, de pe butonul cardului si la
 * tragerea in „Finalizate" (Madalin, 29.10.2026) — altfel nota se pierdea pe tablou.
 */
export const FEREASTRA_GATA = {
  titlu: 'Gata',
  mesaj: 'Ce s-a făcut, pe scurt — rămâne în traseu și în Arhivă. Poți lăsa gol.',
  eticheta: 'Notă (opțional)',
  buton: 'Gata',
}

/** De unde a venit task-ul, in cuvinte scurte (fata cardului). */
export function sursaScurta(t: Pick<CardTask, 'source'>): string | null {
  switch (t.source) {
    case 'email_auto':
    case 'email':
    case 'imap':
      return 'email'
    case 'system':
      return 'automat'
    default:
      return null
  }
}

/** Textele ferestrelor de arhivare și ștergere — aceleași pe card și în panoul task-ului. */
export const MESAJ_ARHIVARE =
  'Iese de pe tablou și merge în Arhivă, la „Arhivate”, cu comentariile, documentele și tot traseul. Nu se șterge. Poți spune de ce.'
export const MESAJ_STERGERE =
  'Dispare de peste tot — tablou, Gata, Arhivă —, cu comentariile, documentele și traseul lui. Nu se poate recupera. Dacă doar nu se mai face, folosește „Arhivează”.'
